// src/services/questionFactory/providers/providerBase.js
//
// Abstract provider interface for Question Factory generation sources.
//
// Phase 1 contract — every provider exports a default object with this shape:
//
//   {
//     id:          string,            // 'claude' | 'chatgpt' | 'pmi_infinity' | 'gemini'
//     displayName: string,             // 'Claude' | 'ChatGPT' | 'PMI Infinity' | 'Gemini'
//     enabled:     boolean,            // false until Phase 2 wires the real API
//     supportedModes: ['generate' | 'review'],
//     defaultModel: string | null,
//     async generate(opts): Promise<GenerateResult>
//     async review  (opts): Promise<ReviewResult>
//   }
//
// GenerateResult:
//   {
//     drafts: Array<{
//       draftId: string,
//       question: object,              // canonical question shape (see docs)
//     }>,
//     tokens:  { input: number, output: number },
//     cost:    { estimated_usd: number | null },
//     meta:    object,                 // raw provider metadata for audit
//   }
//
// ReviewResult:
//   {
//     score:   number,                 // 0–10
//     passed:  boolean,
//     comments: string,
//     rubric:  object,                 // per-criterion subscores
//     tokens:  { input: number, output: number },
//   }
//
// Phase 1 providers MUST NOT make real network calls — they return
// deterministic stub data. Phase 2 swaps the stub bodies for real API calls
// (Anthropic, OpenAI, PMI Infinity SSE) while keeping the interface stable.

/**
 * Default options accepted by every provider's generate() / review() calls.
 * Documented here so Phase 2 implementations stay aligned.
 *
 * generate(opts):
 *   examId:        'pmp' | 'pmi-acp' | 'cpmai'
 *   count:         integer (target question count)
 *   domain:        string | null     (e.g. 'Planning')
 *   model:         string | null     (override defaultModel)
 *   prompt:        string | null     (custom prompt template id)
 *   options:       object            (free-form provider knobs)
 *
 * review(opts):
 *   question:      object            (canonical question to evaluate)
 *   model:         string | null
 *   rubric:        object            (criteria + weights)
 */

export class ProviderError extends Error {
  constructor(message, { provider, cause } = {}) {
    super(message);
    this.name = 'ProviderError';
    this.provider = provider || null;
    this.cause = cause || null;
  }
}

/**
 * Throws if the provider isn't enabled yet. Convenience for Phase 1 stubs
 * that haven't been wired to a real API.
 */
export function requireEnabled(provider) {
  if (!provider || !provider.enabled) {
    throw new ProviderError(
      `Provider "${provider?.id || 'unknown'}" is not enabled in this phase.`,
      { provider: provider?.id }
    );
  }
}

/**
 * Validate that a generate() call's opts are well-formed.
 * Returns the (possibly defaulted) opts object or throws.
 */
export function normalizeGenerateOpts(opts = {}) {
  if (!opts.examId) {
    throw new ProviderError('generate(): examId is required');
  }
  return {
    examId:  String(opts.examId).toLowerCase().replace(/_/g, '-'),
    count:   Math.max(1, Math.min(parseInt(opts.count, 10) || 1, 500)),
    domain:  opts.domain || null,
    model:   opts.model  || null,
    prompt:  opts.prompt || null,
    options: opts.options || {},
  };
}

const providerBaseModule = {
  ProviderError,
  requireEnabled,
  normalizeGenerateOpts,
};

export default providerBaseModule;
